import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Option,
  Radio,
  RadioGroup,
  Select,
  Sheet,
  Typography,
} from "@mui/joy";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  PortablePipeline,
  parsePortablePipeline,
} from "../../entities/pipeline/portablePipeline";
import {
  formOpenSelector,
  selectedPipelineIDSelector,
  setFormOpen,
  setSelectedPipelineID,
  setSelectedStageID,
} from "../../reducers/rhapsody";
import { usePipelines } from "../../redux/pipeline/pipelineHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";
import { RouterConfig } from "../config/routerConfig";
import DialogClose from "../dialogClose/dialogClose";
import Id from "../id/id";
import {
  ImportMode,
  ImportResult,
  usePipelineImport,
} from "./usePipelineImport";

/**
 *
 * @returns {ReactElement} The pipeline import dialog.
 */
export function PipelineImport() {
  const open = useSelector(formOpenSelector("pipelineImport"));
  const selectedPipelineID = useSelector(selectedPipelineIDSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const askQuestion = useAskQuestion();
  const { pipelines } = usePipelines();
  const { runImport, running, progress } = usePipelineImport();

  const [payload, setPayload] = useState<PortablePipeline | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mode, setMode] = useState<ImportMode>("create");
  const [targetPipelineID, setTargetPipelineID] = useState<
    number | undefined
  >();
  const [result, setResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const close = () => {
    if (running) return;
    dispatch(setFormOpen({ isOpen: false, formID: "pipelineImport" }));
  };

  // Reset everything whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setPayload(null);
      setFileError(null);
      setResult(null);
      setImportError(null);
      setMode("create");
      setTargetPipelineID(undefined);
    }
  }, [open]);

  // An override import is not atomic (it deletes then recreates). Warn before
  // a navigation/refresh while it runs so a half-applied pipeline is less
  // likely to be left behind by an accidental tab close.
  useEffect(() => {
    if (!running) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [running]);

  const onFile = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setFileError(null);
    try {
      const parsed = parsePortablePipeline(await file.text());
      setPayload(parsed);

      // Make overriding the obvious match effortless: if we are already inside a
      // pipeline, or a same-named pipeline exists, pre-select it for override.
      const sameName = pipelines?.find(
        (p) => p.name && p.name === parsed.pipeline.name
      );
      if (selectedPipelineID) {
        setMode("override");
        setTargetPipelineID(selectedPipelineID);
      } else if (sameName) {
        setMode("override");
        setTargetPipelineID(sameName.id);
      } else {
        setMode("create");
        setTargetPipelineID(undefined);
      }
    } catch (e) {
      setPayload(null);
      setFileError(e instanceof Error ? e.message : "Could not read the file.");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onFile,
    multiple: false,
    accept: { "application/json": [".json"] },
  });

  const stageCount = payload?.pipeline.stages.length ?? 0;
  const interactorCount = useMemo(
    () =>
      payload?.pipeline.stages.reduce(
        (total, s) => total + (s.interactors?.length ?? 0),
        0
      ) ?? 0,
    [payload]
  );

  const targetPipeline = pipelines?.find((p) => p.id === targetPipelineID);

  const canImport =
    !!payload && (mode === "create" || targetPipelineID != null) && !running;

  const doImport = async () => {
    if (!payload) return;

    if (mode === "override") {
      const confirmed = await askQuestion(
        "Override this pipeline?",
        ["Cancel", "Override"],
        {
          subtitle: (
            <Typography>
              All current stages and interactors of{" "}
              <b>{targetPipeline?.name ?? "the selected pipeline"}</b> will be
              deleted and replaced with the imported configuration. This cannot
              be undone, and if the import is interrupted the pipeline may be
              left partially configured — keep this tab open until it finishes.
            </Typography>
          ),
        }
      );
      if (confirmed !== 1) return;
    }

    setImportError(null);
    try {
      const res = await runImport({ payload, mode, targetPipelineID });
      setResult(res);
    } catch (e) {
      setImportError(
        e instanceof Error ? e.message : "The import did not complete."
      );
    }
  };

  const openResult = () => {
    if (!result) return;
    dispatch(setSelectedStageID(null));
    dispatch(setSelectedPipelineID(result.pipelineID));
    navigate(`${RouterConfig.pipelines}/${result.pipelineID}`);
    close();
  };

  return (
    <DialogClose open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography
          level="title-lg"
          startDecorator={<i className="fa-solid fa-file-import"></i>}
        >
          Import Pipeline
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {result ? (
            <Alert color="success" variant="soft">
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography level="title-sm" sx={{ color: "inherit" }}>
                  Import complete
                </Typography>
                <Typography level="body-sm" sx={{ color: "inherit" }}>
                  {result.stagesCreated} stage(s) and{" "}
                  {result.interactorsCreated} interactor(s) created
                  {result.stagesDeleted > 0
                    ? `, ${result.stagesDeleted} stage(s) replaced`
                    : ""}
                  .
                </Typography>
              </Box>
            </Alert>
          ) : (
            <>
              {/* File picker */}
              <div
                {...getRootProps()}
                style={{
                  border: "1px dashed",
                  borderColor: isDragActive ? "#2196f3" : "#cdd7e1",
                  borderRadius: 8,
                  padding: 24,
                  textAlign: "center",
                  cursor: "pointer",
                  background: isDragActive ? "#2196f311" : "#fafafa",
                }}
              >
                <input {...getInputProps()} />
                <Typography
                  level="body-sm"
                  startDecorator={
                    <i className="fa-solid fa-cloud-arrow-up"></i>
                  }
                  sx={{ justifyContent: "center" }}
                >
                  {payload
                    ? "Drop another file to replace"
                    : "Drop a pipeline .json file here, or click to browse"}
                </Typography>
              </div>

              {fileError ? (
                <Alert color="danger" variant="soft">
                  {fileError}
                </Alert>
              ) : null}

              {payload ? (
                <>
                  <Sheet
                    variant="soft"
                    sx={{
                      borderRadius: 8,
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <Typography level="title-sm">
                      {payload.pipeline.name ?? "Untitled pipeline"}
                    </Typography>
                    <Typography level="body-xs">
                      {stageCount} stage(s) · {interactorCount} interactor(s)
                      {payload.exportedAt
                        ? ` · exported ${new Date(
                            payload.exportedAt
                          ).toLocaleString()}`
                        : ""}
                    </Typography>
                  </Sheet>

                  <RadioGroup
                    value={mode}
                    onChange={(e) => setMode(e.target.value as ImportMode)}
                  >
                    <Radio
                      value="create"
                      label="Create a new pipeline"
                      disabled={running}
                    />
                    <Radio
                      value="override"
                      label="Override an existing pipeline"
                      disabled={running}
                    />
                  </RadioGroup>

                  {mode === "override" ? (
                    <Select
                      placeholder="Select a pipeline to override…"
                      value={targetPipelineID ?? null}
                      disabled={running}
                      onChange={(e, v) =>
                        setTargetPipelineID((v as number) ?? undefined)
                      }
                    >
                      {pipelines?.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {p.name}
                          <Id>{p.id}</Id>
                        </Option>
                      ))}
                    </Select>
                  ) : null}

                  <Alert color="warning" variant="soft" size="sm">
                    <Typography level="body-xs" sx={{ color: "inherit" }}>
                      Inherited pipelines and excluded stage/interactor
                      references point to other pipelines and are copied as-is —
                      verify them after import if the target environment
                      differs.
                    </Typography>
                  </Alert>
                </>
              ) : null}

              {importError ? (
                <Alert color="danger" variant="soft">
                  {importError}
                </Alert>
              ) : null}

              {running && progress ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography level="body-sm">
                    {progress.phase}
                    {progress.total > 0
                      ? ` (${progress.current}/${progress.total})`
                      : ""}
                  </Typography>
                  <LinearProgress
                    determinate={progress.total > 0}
                    value={
                      progress.total > 0
                        ? (progress.current / progress.total) * 100
                        : undefined
                    }
                  />
                </Box>
              ) : null}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            <Button variant="soft" color="neutral" onClick={close}>
              Close
            </Button>
            <Button
              onClick={openResult}
              endDecorator={<i className="fa-solid fa-arrow-right"></i>}
            >
              Open pipeline
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="soft"
              color="neutral"
              onClick={close}
              disabled={running}
            >
              Cancel
            </Button>
            <Button
              loading={running}
              disabled={!canImport}
              color={mode === "override" ? "danger" : "primary"}
              onClick={doImport}
            >
              {mode === "override" ? "Override pipeline" : "Create pipeline"}
            </Button>
          </>
        )}
      </DialogActions>
    </DialogClose>
  );
}
